// src/components/desk/ShareTab.jsx — the reshare tab (B244, owner ruling 2026-07-31)
// ----------------------------------------------------------------------------
// "Agent sees all the info that will be reshared to platforms, and below the buttons
// korter / ss / myhome as we have in the extension — then it acts as the extension's
// buttons and reshares this data. If the user leaves everything as it is that's fine;
// if they change it, the reshare takes those values."
//
// ── WHAT THIS SCREEN PROMISES ───────────────────────────────────────────────
// It shows THE HANDOVER — the payload we pass to the extension — and nothing more.
// It deliberately does NOT claim to show what korter/ss/myhome will end up displaying:
// the extension still applies admin forced chips, the description-cleaner, the
// field→description fallback for fields a portal has no input for, and the two-level
// per-platform property-type selection. Owner ruling: "let modal show what it'll send
// to extension, then extension will do its job." A preview that promised the final ad
// would be lying on every platform at once.
//
// ── THE PAYLOAD IS ALREADY THE AGENT'S OWN VERSION ──────────────────────────
// The server overlays this agent's own property edits (agent_property_view state==='own')
// on top of the origin capture before we ever see it, so an agent who edited nothing
// reposts the original ad unchanged, and an agent who edited gets their values — with no
// rule duplicated here. Provenance dots say which is which.
//
// ── EDITS HERE ARE FOR THIS REPOST ONLY ─────────────────────────────────────
// Changing a value in this form changes what we hand the extension. It does NOT write an
// agent_property_endorsement or a claim — recording "this is the true value of this
// property" is a different act with different meaning (and a different door: the property
// modal's edit). Conflating them would turn every ad-copy tweak into a permanent factual
// assertion about someone else's flat.

import { useEffect, useMemo, useRef, useState } from 'react'
import { t } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { card, input, btn } from './primitives.jsx'
import { getResharePayload, getOrpheusRenders, getPropertyShareContacts, registerReshare, queueExtJob, getExtJob } from '../../db/core.js'
import { getAccessToken } from '../../db/index.js'
import { pingExtension, startReshare, startFacebookShare, supportsFbShare, drainPendingReshares } from '../../lib/extensionBridge.js'
import { fbShareTexts } from '../../lib/fbSharePayload.js'
import { fbGroupForKind } from '../../lib/fbGroups.js'
import { fbShareContacts } from '../../lib/fbShareContacts.js'
import { AdvertisingHistory } from './AdvertisingHistory.jsx'
import { BrowserSessions } from './BrowserSessions.jsx'
import { SharePlanner } from './SharePlanner.jsx'
import { requiredFor, blockers, CHOICES, isMissing } from '../../../core/reshare-readiness.mjs'
import AiMark from './AiMark.jsx'
import { convertMoney } from '../../lib/money.js'
import { useUsdGelRate } from '../../lib/fx.js'

// The Desk session's own login, read from the JWT we ALREADY hold.
// ⚠️ Deliberately not supabase.auth.getUser()/getSession(): a per-render auth call on this
// path is what deadlocked the whole Desk behind gotrue's refresh lock once before. This
// decodes the cached token instead — no network, no lock.
async function deskAccount() {
  try {
    const tok = await getAccessToken()
    if (!tok) return null
    const body = JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return body?.email || null
  } catch { return null }
}

// Compare logins, not display names: "gvantsak@sentavus.com" vs "gvantsak" must count as the
// same person, and case must not matter.
const sameAccount = (a, b) => {
  if (!a || !b) return null                       // unknown — caller decides, never "equal"
  const norm = s => String(s).trim().toLowerCase().split('@')[0]
  return norm(a) === norm(b)
}

// Stale-bridge auto-reload bookkeeping (owner 2026-09-28) — see STALE → RELOAD in ShareTab.
// sessionStorage: per tab, survives the reload, gone with the tab.
const STALE_RELOAD_KEY = 'sntv-share-stale-reload-at'
const SHARE_RESUME_KEY = 'sntv-share-resume'
// One automatic reload per minute. Storage unavailable → no auto-reload at all, because
// without the marker nothing could stop a loop.
function staleReloadAllowed() {
  try { return Date.now() - (Number(sessionStorage.getItem(STALE_RELOAD_KEY)) || 0) > 60 * 1000 }
  catch { return false }
}
function saveShareResume(listingId, target) {
  try { sessionStorage.setItem(SHARE_RESUME_KEY, JSON.stringify({ listingId, target, at: Date.now() })) } catch { /* private mode */ }
}
// Returns the interrupted target ONCE, and only for this listing within 2 minutes — an old
// marker must never fire a repost the agent has since forgotten about.
function takeShareResume(listingId) {
  try {
    const r = JSON.parse(sessionStorage.getItem(SHARE_RESUME_KEY) || 'null')
    if (!r) return null
    sessionStorage.removeItem(SHARE_RESUME_KEY)
    return r.listingId === listingId && Date.now() - r.at < 2 * 60 * 1000 ? r.target : null
  } catch { return null }
}

// The three portals, in the extension popup's own order so the two surfaces read the same.
// Values are handleTransfer's `targetSite` strings verbatim — nothing translates on the way.
const TARGETS = [
  { key: 'korter', label: 'Korter', tone: 'text-amber-500' },
  { key: 'ss',     label: 'SS.ge',  tone: 'text-red-500' },
  { key: 'myhome', label: 'MyHome', tone: 'text-emerald-500' },
]

// Editable numeric/text fields, in the order an agent reads a listing. `k` is the payload
// key the fillers read (schema.js UniversalPropertyData) — never renamed on the way through.
const EDIT_FIELDS = [
  { k: 'price',         label: 'desk_lineage_field_price', numeric: true },
  { k: 'roomsCount',    label: 'desk_lineage_field_rooms', numeric: true },
  { k: 'bedroomsCount', label: 'desk_share_f_bedrooms',    numeric: true },
  { k: 'bathroomCount', label: 'desk_share_f_bathrooms',   numeric: true },
  { k: 'areaSqM',       label: 'desk_lineage_field_area',  numeric: true },
  { k: 'landAreaSqM',   label: 'desk_share_f_land_area',   numeric: true },
  { k: 'aptFloor',      label: 'desk_share_f_floor',       numeric: true },
  { k: 'totalFloors',   label: 'desk_share_f_floors_total', numeric: true },
  { k: 'ceilingHeight', label: 'desk_share_f_ceiling',     numeric: true },
]

// ── PHONE JOB AS A STEP LIST (owner 2026-10-09: "step list per portal") ──────────────
// The server reports states; the phone keeps the FURTHEST one it has seen (`reached`), because
// a 3-second poll can jump straight from queued to publishing. 'filled' counts as the end of the
// filling step. Paying is shown only when the portal actually charged (ss, myhome past 3 free).
const JOB_RANK = { queued: 1, claimed: 2, running: 3, filled: 4, publishing: 4, paying: 5, published: 6 }
const JOB_STEPS = [
  { key: 'sent',    label: 'desk_share_step_sent',    done: 1 },
  { key: 'picked',  label: 'desk_share_step_picked',  done: 2 },
  { key: 'filling', label: 'desk_share_step_filling', done: 4 },
  { key: 'publish', label: 'desk_share_step_publish', done: 5 },
  { key: 'paying',  label: 'desk_share_step_paying',  done: 6, onlyIfPaid: true },
  { key: 'done',    label: 'desk_share_step_done',    done: 6 },
]
function JobSteps({ portal, r }) {
  const failed  = r.job.state === 'error' || r.job.state === 'expired'
  const reached = Math.max(r.reached ?? 0, JOB_RANK[r.job.state] ?? 0)
  const steps   = JOB_STEPS.filter(s => !s.onlyIfPaid || r.paid)
  // The step in progress (or where it stopped): the first one not yet done.
  const current = steps.findIndex(s => reached < s.done)
  const link    = r.job.state === 'published' && /^https?:\/\//.test(r.job.message || '') ? r.job.message : null
  const detail  = ['running', 'publishing', 'paying'].includes(r.job.state) ? r.job.message : null
  return (
    <div className="mt-2 text-xs">
      <p className="font-medium">{portal} <span className="text-muted-foreground font-normal">· {r.dev}</span></p>
      <ol className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
        {steps.map((s, i) => {
          const state = i < current || current === -1 ? 'done' : i === current ? (failed ? 'fail' : 'now') : 'todo'
          return (
            <li key={s.key} className={state === 'done' ? 'text-emerald-600' : state === 'fail' ? 'text-destructive font-medium'
              : state === 'now' ? 'text-foreground font-medium' : 'text-muted-foreground'}>
              {state === 'done' ? '✓' : state === 'fail' ? '✗' : state === 'now' ? '…' : '○'} {t(s.label)}
            </li>
          )
        })}
      </ol>
      {detail && <p className="text-muted-foreground mt-0.5">— {detail}</p>}
      {failed && <p className="text-destructive mt-0.5">{r.job.state === 'expired'
        ? t('desk_share_job_expired').replace('{d}', r.dev || '') : r.job.message}</p>}
      {link && <a className="underline text-emerald-600" href={link} target="_blank" rel="noreferrer">{t('desk_share_job_open')}</a>}
    </div>
  )
}

// ── WHAT EACH PORTAL REFUSES TO PUBLISH WITHOUT (owner 2026-10-09: "ask the user the details
// that are missing BEFORE reshare so it won't get stuck"). Checked on press, before anything is
// sent — a fill that reaches the portal without these stops at the portal's own validation,
// which on a remote (phone) share happens on a screen the agent cannot see.
// Observed live: myhome refused a street with no house number (houseNo=null, 2026-10-09).
// Keys are payload keys the fillers read. Labels reuse EDIT_FIELDS where one exists.
// ⚠️ READ OFF THE LIVE FORMS, every deal × type, 2026-10-09 (fields marked * on myhome and ss;
// korter marks none and only complains after the press — its messages reach the phone then;
// it does refuse a missing house number, agent-reported 2026-10-10, so that one is asked).
//   myhome: status (land: land status), condition (not land), rooms (not land), floor (flat,
//           commercial, hotel), total floors (not land), area, price, location+number, description
//           — commercial's status row is its sub-type, which the filler picks from the type.
//   ss:     status (land: land status) on every type, rooms (flat/house/hotel), floor (flat),
//           condition + yard area (house/cottage), area, price.
// HOUSE NUMBER ON ALL THREE (owner 2026-10-14: "ss, korter and myhome ask street number"): ss does
// not mark it required, but fill_ss.js types payload.streetNumber and a repost without one is a
// weaker ad — so it sits in the base list beside the street, asked on every portal when missing.
// Project type and contact are filled by the extension itself; a pledge's extra fields (period,
// who lives there) are not asked yet.
// The rules themselves live in core/reshare-readiness.mjs since 2026-10-17 (read off each portal's
// own form code; the server's automation check reads the byte-identical twin). Do not add a
// rule here — a second copy is what asked land plots apartment questions on 2026-10-16.
// Required CHOICE fields: asked as a pick, stored as the tag the fillers already read
// (mappings.js normal_keys), and "present" when tagKeys hold any of the options.
// CHOICES (the tag vocabularies) and missingValue come from core/reshare-readiness.mjs — the server
// must judge "missing" exactly as this tab does.
// Required choices stored straight on the payload key (not as a tag) — the fillers read
// payload.dealType / payload.propertyType in the extension's own vocabulary (schema.js).
const FIELD_CHOICES = {
  dealType: [['trade', 'იყიდება'], ['lease', 'ქირავდება'], ['daily_rent', 'ქირავდება დღიურად'], ['girao', 'გირავდება']],
  propertyType: [['apartment', 'ბინა'], ['house', 'სახლი'], ['commercial', 'კომერციული ფართი'], ['land', 'მიწის ნაკვეთი'], ['hotel', 'სასტუმრო']],
}
// The order the up-front box asks in: deal and type first (they decide the rest), then place,
// money, size, statuses, layout, text.
const ASK_ORDER = ['dealType', 'propertyType', 'city', 'streetName', 'streetNumber', 'price', 'areaSqM', 'landAreaSqM',
  'landStatus', 'buildingStatus', 'condition', 'roomsCount', 'bedroomsCount', 'aptFloor', 'totalFloors', 'description']
const askRank = k => { const i = ASK_ORDER.indexOf(k); return i === -1 ? ASK_ORDER.length : i }
// A blocker in the agent's language; the module's English message is the fallback for a new code.
const blockText = (b, portal) => {
  const label = { myhome: 'MyHome', ss: 'SS.ge', korter: 'Korter' }[portal] || portal
  const k = `desk_share_block_${b.code}`, s = t(k)
  return s === k ? `${label}: ${b.message}` : s.replace('{p}', label)
}
const missingValue = isMissing
const NEED_LABEL = {
  dealType: 'desk_share_f_deal', propertyType: 'desk_share_f_kind', city: 'desk_share_f_city',
  streetName: 'desk_share_f_street', streetNumber: 'desk_share_f_street_number',
  buildingStatus: 'desk_share_f_building_status',
  condition: 'desk_share_f_condition', landStatus: 'desk_share_f_land_status',
  description: 'desk_share_f_description',
  ...Object.fromEntries(EDIT_FIELDS.map(f => [f.k, f.label])),
}
const isBlank = v => v == null || (typeof v === 'string' && !v.trim())

// Where a value came from. Shown as a dot, not a word — nine labelled badges would drown
// the form, but an agent still needs to know which numbers are the AI's guesses.
const PROV = {
  own:     { cls: 'bg-primary',      key: 'desk_share_prov_own' },
  ai:      { cls: 'bg-amber-500',    key: 'desk_share_prov_ai' },
  origin:  { cls: 'bg-muted-foreground/50', key: 'desk_share_prov_origin' },
  listing: { cls: 'bg-muted-foreground/50', key: 'desk_share_prov_origin' },
}

function ProvDot({ kind }) {
  const p = PROV[kind]
  if (!p) return null
  return <span title={t(p.key)} className={`inline-block w-1.5 h-1.5 rounded-full align-middle ${p.cls}`} />
}

// ── THE THIRD PRICE (owner ruling 2026-08-01) ───────────────────────────────────────
// ad price (owner's, from the scrape) → agreed price (cooperation) → THIS price.
// agreedPrice/agreedCurrency carry link 2. The repost price INHERITS the agreed price
// while the agent has not touched it, and falls back to the ad's own price when there
// is no agreed one; the moment the agent types here it is their value and no upstream
// change may overwrite it. The inheritance is DERIVED at render (see `effPrice`), never
// written into `form` — writing it would mark the field hand-edited and freeze it.
// ⚠️ Inheriting is not syncing: nothing flows back the other way, because this number is
// marketing copy for one repost and the agreed price is a recorded fact about the
// mandate. Under the net_amount commission shape they are SUPPOSED to differ (the
// advertised price sits above the owner's net, and the spread is the agent's fee).
// onBeforeRun: DealEditor's save-guard — flushes dirty cooperation terms before a portal
// button fires. Throws → the reshare is aborted and the error shows on that button.
// ⚠️ originListingId — BUILD THE PAYLOAD FROM THE ORIGIN, NOT FROM OUR OWN REPOST
// (agent-reported 2026-08-01: "sometimes images don't load in reshare window").
// /api/core/reshare-payload reads raw_listing by the natural key (source, source_listing_id)
// of whatever listing id it is handed, and never walks lineage. Hand it one of OUR reposts
// and it reads that repost's own raw rows — which are written by the post-publish snapshot
// ingest in sentavus_wiring.js, and that call sends price/area/rooms/floor/address but NO
// images. So selectImages found nothing and the tab said "0 photos" on a property whose card
// was visibly showing seven. It looked intermittent only because it depends on WHICH row the
// agent pressed the repost door from: the origin ad works, their own Korter/SS repost does not.
// Reposting from the origin is also the correct semantics — a repost's payload is MARKETING
// (what we chose to publish), not OBSERVATION (what the source ad says), and this file's own
// header says feeding it back in would let our edits masquerade as the source's facts. It
// additionally keeps `originSourceUrl` pointing at the true origin, which is what
// register_reshare needs to record lineage against a real platform id.
// The durable fix is server-side (walk derived_from there, so no caller can get this wrong);
// this resolves it at the one place every caller already passes through.
export function ShareTab({ listingId, originListingId = null, agentId, onClose, agreedPrice = null, agreedCurrency = null, onBeforeRun = null }) {
  // The id we actually build from. Falls back to the listing itself if the origin turns out
  // to be unfetchable (deleted, or not visible to this agent) — a photo-less form still beats
  // an error where a form used to be.
  const [srcId, setSrcId] = useState(originListingId || listingId)
  // ── Facebook composer state (B894) — no extension involved, read+render only ──
  const [fb, setFb] = useState(null)          // { renders: [{target,text,cached}] }
  const [fbBusy, setFbBusy] = useState(false)
  const [fbErr, setFbErr] = useState(null)
  const [fbLang, setFbLang] = useState('fb_ka')
  const [fbCopied, setFbCopied] = useState(false)
  // The share MENU (owner 2026-08-20): contact / price / exact location are tick-offable;
  // unticked = absent from the text. "Exact location" = the street/building NUMBER — the
  // street itself stays. The agent's phone is remembered locally (typed once).
  const [fbMenu, setFbMenu] = useState(false)
  const [fbOpts, setFbOpts] = useState({ contact: true, price: true, exact: true })
  const [fbPhone, setFbPhone] = useState(() => { try { return localStorage.getItem('sntv_share_contact') || '' } catch { return '' } })
  // §7: who may be published, built and FILTERED server-side (T27 opt-outs).
  const [fbContacts, setFbContacts] = useState(null)   // null = not asked yet
  const [fbPick, setFbPick] = useState([])             // party_ids the sharer chose
  // Final-page overrides for deal / property type / condition (owner ruling 2026-08-26: "the
  // last page is the most overrider — anything changed before sharing must be used", and the
  // agent may correct a mislabelled type here: apartment↔commercial, house→land). '' means
  // "as captured" and sends NOTHING — only a deliberate pick becomes a verified overlay fact.
  // They feed card_fields (the Orpheus fact package) AND, since 2026-10-29, the portal repost:
  // `outbound` translates them into the extension's vocabulary (see the ⚠️ there).
  const [finalDeal, setFinalDeal] = useState('')
  const [finalKind, setFinalKind] = useState('')
  const [finalCond, setFinalCond] = useState('')
  // land only (owner 2026-08-26): agricultural / non-agricultural status — a plot has no
  // "condition", so when the effective kind is land this replaces the condition select.
  const [finalLand, setFinalLand] = useState('')
  // The plot's UNIT (migration 0724). '' = unknown and is sent as nothing — a plot quoted in
  // ari published as m² is 100x wrong, so silence beats a default.
  const [finalLandUnit, setFinalLandUnit] = useState('')
  const [fbOpened, setFbOpened] = useState(null)   // 'sending' | {images} | {error}
  useEffect(() => { setSrcId(originListingId || listingId) }, [originListingId, listingId])
  const [loading, setLoading] = useState(true)
  const [err, setErr]         = useState(null)
  const [data, setData]       = useState(null)     // server response
  const [form, setForm]       = useState(null)     // editable copy of data.payload
  const [ext, setExt]         = useState(null)     // { present, account } — null = still checking
  const [deskAcc, setDeskAcc] = useState(null)     // this Desk session's login
  const [deskAccReady, setDeskAccReady] = useState(false)   // lookup finished (deskAcc may still be null)
  // The set of targets currently in flight — not a single key (owner request 2026-08-01:
  // "can we allow another platform reshare in parallel?").
  // ⚠️ The old one-at-a-time rule was stricter than the extension needs, and its stated
  // reason was wrong: it claimed "the extension holds a per-platform lock anyway, firing
  // three at once would just queue them". `acquirePlatformLock` keys on locks[platform], and
  // myhome is multi-slot — so different portals never queued, they were simply never allowed
  // to start. Same platform twice IS still refused, which is why this is a set of targets
  // rather than a boolean: each button locks only itself.
  const [busy, setBusy]       = useState(() => new Set())
  const [results, setResults] = useState({})       // { target: {ok} | {error} | {job} }
  const [need, setNeed] = useState(null)             // { target, keys: [...], noPhotos } — see requiredFor
  // Remote share (phase 2): the agent's browsers that run the extension, and which one a press
  // from THIS page (a phone — no extension here) is sent to. See BrowserSessions.jsx.
  const [browsers, setBrowsers] = useState([])
  const [deviceId, setDeviceId] = useState(null)
  // ONE PHONE JOB AT A TIME (owner 2026-10-09): the PC fills only in its ACTIVE tab, so a second
  // job beside the first stalls. While one is unfinished every portal button waits for it; the
  // server enforces the same (409 busy) for presses from another tab or phone.
  const runningJob = Object.entries(results).find(([, r]) => r?.job && !['published', 'error', 'expired'].includes(r.job.state))
  const remoteDevice = browsers.find(b => b.online && b.device_id === deviceId)
    || (deviceId ? null : browsers.find(b => b.online)) || null
  // Photos the agent excluded from THIS repost (owner request 2026-07-31: review the
  // gallery before sharing, remove what shouldn't go). A Set of urls rather than a
  // filtered copy of form.imageUrls, so exclusion is reversible in place — click again
  // to restore — and the server's list stays the single source of what exists.
  const [removed, setRemoved] = useState(() => new Set())
  // PHOTO ORDER (owner 2026-10-10: "allow the agent from mobile to relocate images"). korter
  // refuses a main photo that is not the flat's inside, and every portal shows the first photo
  // as the cover — so the agent picks the order here. null = the server's order. Stored as the
  // urls in the agent's order; photos the server adds later are appended, ones it drops vanish.
  const [order, setOrder] = useState(null)
  const [reordering, setReordering] = useState(false)
  // Phone only: on a PC the agent watches the portal tab and reorders there themselves
  // (owner 2026-10-16). ext === null = still checking → hidden. `installed` too: a PC whose
  // extension did not answer (stale tab, slow worker) is still a PC, not a phone.
  const canReorder = ext?.present === false && !ext.installed
  const reorderOn = reordering && canReorder
  // Draft persistence (owner ruling 2026-08-01): edits here survive closing/reopening the
  // modal AND the next repost of the same ad — "next time it must inherit what was filled
  // in each field". Only the fields the agent actually TOUCHED are stored; the rest keep
  // following the server payload, so a fresh AI description or a corrected origin value is
  // never shadowed by a stale full-form snapshot.
  // ⚠️ Deliberately localStorage, not the server: these are repost preferences, NOT claims
  // about the property (see the header) — writing them server-side would turn every ad-copy
  // tweak into a factual assertion. Consequence to know: they are per-BROWSER, so the same
  // agent on another machine gets the inherited values, not their last edit. TTL is long
  // (90d) rather than absent so abandoned drafts cannot accumulate forever.
  const [touched, setTouched]   = useState({})
  const [restored, setRestored] = useState(false)
  const [note, setNote]         = useState(null)   // e.g. "cooperation saved" after the guard fired
  const draftKey = `sntv_share_draft:${srcId}:${agentId || ''}`
  // Has this listing's payload ever finished loading in this browser? Only the FIRST open
  // pays for a real AI analysis; later opens hit the server's content-hashed ai_cache and
  // come back without calling the model. The old loading screen told every open it was
  // "the first" — a warning that cried wolf, which is how a normal 1s wait started reading
  // as "it is re-running the AI again". Remembering the listing lets the message be true.
  const seenKey = `sntv_share_seen:${srcId}`
  const [firstOpen] = useState(() => { try { return !localStorage.getItem(seenKey) } catch { return true } })

  // ── Payload cache: render instantly, confirm before posting (owner request 2026-08-01) ──
  // The payload is rebuilt from the DB on every open and rarely changes, so a re-open used
  // to be a wait for something we already knew. Now the last response is kept per
  // listing+agent and rendered immediately, while a revalidation runs behind it.
  //
  // 🚨 THE SAFETY PROPERTY — a cached payload may be READ but never POSTED unconfirmed.
  // Reposting is what publishes a price to a public portal, so the portal buttons stay
  // disabled until this open's revalidation has come back, and they stay disabled if it
  // FAILS (with a retry) rather than falling back to the cached copy. Failing closed is the
  // whole reason this cache is safe: the worst case is the pre-cache behaviour (a wait),
  // never "advertised yesterday's price because the server was down". The cache buys the
  // reading and editing time, which is where the seconds actually went.
  // 2 (2026-08-02): the payload gained originSource/originSourceListingId. A v1 row cached
  // before this deploy carries neither, and for a manual origin there is no originSourceUrl
  // either — so a cached paint would hand the extension a payload it must refuse. Bumping
  // retires those rows instead of making the agent press "reload listing" to escape.
  const CACHE_VERSION = 2        // bump when the payload SHAPE changes, so old rows are ignored
  const CACHE_TTL_MS  = 24 * 60 * 60 * 1000
  const CACHE_KEEP    = 25       // newest N listings; payloads are ~5–15KB and quota is ~5MB
  // Keys follow the RESOLVED source id, so a draft or a cached payload belongs to the thing
  // actually being reposted from, not to whichever row the door happened to be pressed on.
  const cacheKey = `sntv_share_payload:${srcId}:${agentId || ''}`
  const [revalidating, setRevalidating] = useState(false)
  const [staleErr, setStaleErr]         = useState(null)   // revalidation failed while showing cache
  const [refreshed, setRefreshed]       = useState(false)  // the listing changed under us
  const [reloadTick, setReloadTick]     = useState(0)      // retry button
  // `touched` read from inside the revalidation callback — state there would be the value
  // captured when the request STARTED, silently reverting anything typed while it was in
  // flight (the agent is looking at the form during exactly that window).
  const touchedRef = useRef({})
  useEffect(() => { touchedRef.current = touched }, [touched])
  // In-flight cooperation save shared by every parallel repost — see the ⚠️ in run().
  const guardRef = useRef(null)
  const alive = useRef(true)

  useEffect(() => () => { alive.current = false }, [])

  function readPayloadCache() {
    try {
      const raw = localStorage.getItem(cacheKey)
      if (!raw) return null
      const c = JSON.parse(raw)
      if (c?.v !== CACHE_VERSION || !c?.data) return null
      if (Date.now() - (c.at || 0) > CACHE_TTL_MS) { localStorage.removeItem(cacheKey); return null }
      return c.data
    } catch { return null }
  }
  function writePayloadCache(d) {
    try {
      localStorage.setItem(cacheKey, JSON.stringify({ v: CACHE_VERSION, at: Date.now(), data: d }))
    } catch {
      // Quota: drop the oldest payloads and retry once. Only this cache's own keys are
      // touched — never the drafts beside them, which hold work that cannot be re-fetched.
      try {
        const rows = Object.keys(localStorage)
          .filter(k => k.startsWith('sntv_share_payload:'))
          .map(k => { try { return { k, at: JSON.parse(localStorage.getItem(k))?.at || 0 } } catch { return { k, at: 0 } } })
          .sort((a, b) => b.at - a.at)
        rows.slice(CACHE_KEEP).forEach(r => localStorage.removeItem(r.k))
        localStorage.setItem(cacheKey, JSON.stringify({ v: CACHE_VERSION, at: Date.now(), data: d }))
      } catch { /* give up: the cache is an optimisation, never a requirement */ }
    }
  }

  useEffect(() => {
    if (!srcId) return
    const cached = readPayloadCache()
    if (cached) {
      // Paint from cache, including the draft restore — this is a first application, so it
      // owns touched/removed exactly as a cold load would.
      applyFirstPayload(cached)
      setLoading(false); setRevalidating(true)
    } else {
      setLoading(true)
    }
    setErr(null); setStaleErr(null)
    getResharePayload(srcId, agentId)
      .then(d => {
        if (!alive.current) return
        writePayloadCache(d)
        try { localStorage.setItem(seenKey, String(Date.now())) } catch {}
        if (cached) {
          // Revalidation: the server's values flow in UNDER whatever the agent has typed.
          // Their edits are the one thing a refresh must never overwrite.
          setData(d)
          setForm({ ...(d.payload || {}), ...touchedRef.current })
          if (JSON.stringify(cached.payload) !== JSON.stringify(d.payload)) setRefreshed(true)
        } else {
          applyFirstPayload(d)
        }
        setLoading(false); setRevalidating(false)
      })
      .catch(e => {
        if (!alive.current) return
        // The origin may be gone, or not visible to this agent. Fall back to the listing the
        // door was actually pressed on: a payload with no photos is a worse repost, but an
        // error where a form used to be is a worse product. Re-runs this effect via srcId.
        if (srcId !== listingId) { setSrcId(listingId); return }
        // With a cache on screen this is not a dead end — the form stays usable, only
        // posting is held back (see the 🚨 above).
        if (cached) { setRevalidating(false); setStaleErr(e.message) }
        else { setErr(e.message); setLoading(false) }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcId, agentId, reloadTick])

  // Asked once the property is known. ⚠️ Deliberately NOT gated on the FB menu being open: the
  // list decides whether the share can carry a number at all, and discovering that only after
  // the agent has opened the composer turns a fact into a surprise.
  useEffect(() => {
    const pid = data?.listing?.property_id
    if (!pid) return
    let gone = false
    getPropertyShareContacts(pid)
      .then(d => {
        if (gone) return
        const list = d.contacts || []
        setFbContacts(list)
        // The sharer's own entry is checked; colleagues are not.
        // ⚠️ §7 says "none checked by default" for an admin. Their OWN number is checked anyway,
        // because the alternative is a one-click share that publishes a post nobody can answer —
        // and the owner's stated purpose for this whole feature is that the client is not lost.
        // Every colleague still requires a deliberate tick.
        const mine = list.find(c => c.agent_id && c.agent_id === agentId && c.phone)
        setFbPick(mine ? [mine.party_id] : [])
      })
      .catch(() => { if (!gone) setFbContacts([]) })
    return () => { gone = true }
  }, [data?.listing?.property_id, agentId])

  // First application of a payload (cold load, or the cached paint): restores the draft and
  // takes ownership of touched/removed. Revalidation deliberately does NOT run this.
  function applyFirstPayload(d) {
    setData(d)
    let draft = null
    try {
      const raw = localStorage.getItem(draftKey)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed?.fields && (Date.now() - (parsed.at || 0)) <= 90 * 24 * 60 * 60 * 1000) draft = parsed
        else localStorage.removeItem(draftKey)
      }
    } catch {}
    setForm({ ...(d.payload || {}), ...(draft?.fields || {}) })
    if (draft) {
      setTouched(draft.fields || {})
      touchedRef.current = draft.fields || {}
      setRemoved(new Set(draft.removed || []))
      setOrder(Array.isArray(draft.order) ? draft.order : null)
      setRestored(true)
    }
  }

  // Draft exists exactly while there is something the payload alone wouldn't reproduce.
  useEffect(() => {
    if (loading || !form) return
    try {
      if (Object.keys(touched).length || removed.size || order) {
        localStorage.setItem(draftKey, JSON.stringify({ at: Date.now(), fields: touched, removed: [...removed], order }))
      } else localStorage.removeItem(draftKey)
    } catch {}
  }, [touched, removed, order, loading])   // eslint-disable-line react-hooks/exhaustive-deps

  // "Reload listing" (owner request 2026-08-01). Drops the cached payload and re-fetches
  // cold, so the agent has a way to say "no, go and look again" when what they are reading
  // looks stale or wrong. Deliberately NOT touching the draft: their typed edits are the one
  // thing a refresh must never throw away.
  function reloadPayload() {
    try { localStorage.removeItem(cacheKey) } catch {}
    setRefreshed(false); setStaleErr(null)
    setReloadTick(n => n + 1)
  }

  function discardDraft() {
    try { localStorage.removeItem(draftKey) } catch {}
    setForm({ ...(data?.payload || {}) })
    setTouched({}); setRemoved(new Set()); setOrder(null); setRestored(false)
  }

  // An absent extension is an ordinary state to RENDER (with the reason and the fix), never
  // an error — most of this modal is still useful without it.
  // ⚠️ Re-ping when the tab comes back to the foreground. The whole point is that the fix for
  // every one of these states happens in ANOTHER window: installing the extension, reloading
  // it at chrome://extensions, or switching accounts. Pinging once per mount meant the agent
  // did the fix, came back, and the modal still said it was broken — so they closed and
  // reopened it to no effect, because the AI-backed payload fetch reran while the ping did
  // not. Re-checking on focus makes the banner disappear by itself the moment it is untrue.
  useEffect(() => {
    let dead = false
    const check = () => { pingExtension().then(r => { if (!dead && alive.current) setExt(r) }) }
    check()
    const onVis = () => { if (document.visibilityState === 'visible') check() }
    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', onVis)
    return () => { dead = true; window.removeEventListener('focus', check); document.removeEventListener('visibilitychange', onVis) }
  }, [])
  // STALE → RELOAD BY ITSELF, THEN CARRY ON (owner 2026-09-28: "reload, then continue with the
  // button the agent pressed; if it still fails, show a button"). An orphaned bridge (extension
  // reloaded while this tab was open) is the one extension state a page reload always fixes.
  // Every path that detects it (mount/focus ping, portal buttons, FB button) lands in setExt,
  // so this one effect covers them all. Nothing is lost across the reload: typed edits are in
  // the localStorage draft (effect above), and the URL already reopens this card on this tab
  // (`view=share`, deskUrl.js 2026-08-05).
  // ⚠️ ONLY `stale`. Never auto-reload on 'no extension' / 'waking' — a reload cannot fix those
  // and would loop. ⚠️ The 60s guard stops a loop if the bridge comes back stale again; the
  // banner then offers a manual Reload button instead.
  useEffect(() => {
    if (!ext?.stale || !staleReloadAllowed()) return
    try { sessionStorage.setItem(STALE_RELOAD_KEY, String(Date.now())) } catch { /* private mode */ }
    window.location.reload()
  }, [ext])
  useEffect(() => { deskAccount().then(a => { if (alive.current) { setDeskAcc(a); setDeskAccReady(true) } }) }, [])

  // DESK-LOGIN RESHARE CAPTURE (owner 2026-09-08): a reshare made while the agent was signed into Desk
  // but NOT the extension gets stashed by the extension (it cannot authenticate the register call). Drain
  // those and register each with the Desk's OWN verified session — the server attributes the agent from
  // that session, and no token ever crosses the page bus. On mount + a light interval, so a reshare whose
  // portal link is captured a beat after posting still lands. Idempotent server-side by the reshare key.
  useEffect(() => {
    let stop = false
    const drain = async () => {
      try {
        const pending = await drainPendingReshares()
        for (const p of pending) {
          if (stop || !p?.body) continue
          try { await registerReshare(p.body) } catch { /* best-effort — rare, and no worse than today */ }
        }
      } catch { /* extension asleep/absent → nothing to drain */ }
    }
    drain()
    const id = setInterval(drain, 20000)
    return () => { stop = true; clearInterval(id) }
  }, [])

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setTouched(tt => ({ ...tt, [k]: v })) }

  // ── Price inheritance, resolved at render ─────────────────────────────────────────
  // Hand-edit > agreed cooperation price > the ad's own price. `touched.price` is the
  // hand-edit flag: an untouched field keeps following its upstream, so typing 600 in the
  // cooperation tab moves this number with it, and typing here stops that for good.
  //
  // ⚠️ INHERITING CARRIES THE CURRENCY, NOT JUST THE NUMBER (owner ruling 2026-08-01).
  // "600" is meaningless without its unit, so the upstream pair travels together — and
  // that is exactly why the two cases below behave differently when the dropdown moves:
  //   • still inherited → the dropdown is a DISPLAY choice about someone else's amount,
  //     so the amount is CONVERTED at the NBG rate to stay the same real value.
  //   • hand-typed → the agent stated this number; switching the unit re-denominates what
  //     they typed and must NOT touch the digits. "649" becoming "1752" under their
  //     fingers would be the system overruling a value a human just entered.
  const rate = useUsdGelRate()
  const priceEdited = form ? touched.price !== undefined : false
  // The pair being inherited FROM (never read when priceEdited).
  const upPrice = agreedPrice != null ? agreedPrice : (form?.price ?? '')
  const upCurrency = (agreedPrice != null ? agreedCurrency : form?.priceUnit) || 'USD'
  const wantCurrency = touched.priceUnit !== undefined ? (form?.priceUnit || 'USD') : upCurrency
  // A conversion we cannot do honestly (no rate / non-convertible pair) is REFUSED, not
  // faked: we keep the upstream currency rather than relabel an unconverted amount. See
  // money.js — "never invents a rate".
  const inheritedConverted = priceEdited || upPrice === '' || wantCurrency === upCurrency
    ? null : convertMoney(upPrice, upCurrency, wantCurrency, rate)
  const conversionRefused = !priceEdited && upPrice !== '' && wantCurrency !== upCurrency && inheritedConverted == null
  const effCurrency = !form ? 'USD'
    : priceEdited ? (form.priceUnit || 'USD')
    : conversionRefused ? upCurrency
    : wantCurrency
  const effPrice = !form ? ''
    : priceEdited ? (form.price ?? '')
    : inheritedConverted != null ? Math.round(inheritedConverted)
    : upPrice
  const priceFromCoop = !priceEdited && agreedPrice != null
  // Reset must clear the stored edit as well as the flag, or the field would keep showing
  // the hand-typed number whenever there is no agreed price to fall back to.
  const resetPrice = () => {
    setForm(f => ({ ...f, price: data?.payload?.price ?? null, priceUnit: data?.payload?.priceUnit || 'USD' }))
    setTouched(tt => { const next = { ...tt }; delete next.price; delete next.priceUnit; return next })
  }

  // What actually goes over the bridge: the edited form, plus the identity fields the
  // extension needs to register lineage. originSourceUrl is load-bearing — without it
  // register_reshare has no real platform id for the origin and the repost lands with no
  // derived_from edge at all (the 2026-07-08 phantom-property bug).
  // The gallery in the agent's order (see `order`). Everything that sends photos reads THIS.
  const photos = useMemo(() => {
    const all = form?.imageUrls || []
    if (!order || ext?.present) return all          // PC: the portal's own order (see canReorder)
    const have = new Set(all)
    const kept = order.filter(u => have.has(u))
    const seen = new Set(kept)
    return [...kept, ...all.filter(u => !seen.has(u))]
  }, [form?.imageUrls, order, ext?.present])
  const movePhoto = (u, to) => setOrder(() => {
    const list = photos.filter(x => x !== u)
    list.splice(Math.max(0, Math.min(list.length, to)), 0, u)
    return list
  })

  const outbound = useMemo(() => {
    if (!form || !data) return null
    // The fillers read condition ONLY from tagKeys, as a normal_key. A myhome capture states it
    // in myhome's own wording and the server puts the normalised one in `condition`, so add that
    // one to the tags — otherwise myhome stalls on a condition the listing does state.
    const tags = Array.isArray(form.tagKeys) ? form.tagKeys : []
    let tagKeys = CHOICES.condition.includes(form.condition) && !tags.some(tk => CHOICES.condition.includes(tk))
      ? [...tags, form.condition] : form.tagKeys
    // ⚠️ owner 2026-10-29: "when agent selects commercial instead of flat ... it still selects flat on portals" - the
    // final-page deal / type / condition / land-status picks (below the facts) used to reach the Facebook card only.
    // They are the agent's last word, so the portals take them too, translated into the extension's own vocabulary.
    // The readiness checks run on THIS object, so a type switch also asks for what the new type needs.
    const DEAL_EXT = { sale: 'trade', rent: 'lease', daily_rent: 'daily_rent', girao: 'girao' }
    const LAND_EXT = { agricultural: 'სასოფლო-სამეურნეო', non_agricultural: 'არა სასოფლო-სამეურნეო' }
    const kind = finalKind || form.propertyType
    const over = {}
    if (finalDeal && DEAL_EXT[finalDeal]) over.dealType = DEAL_EXT[finalDeal]
    if (finalKind) over.propertyType = finalKind
    if (finalCond && kind !== 'land') {
      tagKeys = [...(tagKeys || []).filter(tk => !CHOICES.condition.includes(tk)), finalCond]
      over.condition = finalCond
    }
    if (LAND_EXT[finalLand] && kind === 'land') {
      tagKeys = [...(tagKeys || []).filter(tk => !CHOICES.landStatus.includes(tk)), LAND_EXT[finalLand]]
      over.landStatus = LAND_EXT[finalLand]
    }
    return {
      ...form,
      ...over,
      tagKeys,
      // The RESOLVED price, not form.price — inheritance is derived, so the raw form still
      // holds the ad's number while the field displays the agreed one.
      price: effPrice === '' ? null : effPrice,
      priceUnit: effCurrency,
      // Excluded photos drop out HERE, not from form.imageUrls — the gallery below keeps
      // rendering them (dimmed) so the agent can change their mind before firing.
      imageUrls: photos.filter(u => !removed.has(u)),
      source: data.listing?.source ?? form.source ?? null,
      sourceUrl: data.listing?.source_url ?? form.sourceUrl ?? null,
      originSourceUrl: data.listing?.source_url ?? form.originSourceUrl ?? null,
      // The origin's NATURAL KEY, which a manually entered property has and a URL is not
      // (source='manual', source_listing_id='manual:<entry>', source_url=NULL). The
      // extension refuses a repost it cannot record lineage for, and until 2026-08-02 the
      // only lineage road was parsing a platform URL — so manual entries could not be
      // reposted at all. This is what it falls back to; scraped ads carry both and are
      // unaffected. Not renamed on the way through: /api/core/reshare takes these verbatim.
      originSource: data.listing?.source ?? form.originSource ?? null,
      originSourceListingId: data.listing?.source_listing_id ?? form.originSourceListingId ?? null,
      coreListingId: data.listing?.id || null,
      corePropertyId: data.listing?.property_id || null,
    }
  }, [form, data, removed, photos, effPrice, effCurrency, finalDeal, finalKind, finalCond, finalLand])

  // The second half of STALE → RELOAD (above, beside the ping). Placed AFTER `outbound`:
  // the deps array reads it during render, and above its declaration that is a TDZ crash.
  // Re-press the portal button the reload interrupted. SAFE to repeat, and
  // only because of where the marker is written — run() stores it only when the stale ping
  // REFUSED the press, i.e. before startReshare, so the extension never saw the first press
  // and there is nothing to publish twice. Consumed BEFORE run() so it fires at most once.
  // Waits for everything run() would otherwise silently return on (payload, revalidation).
  // ⚠️ And for the Desk login lookup: the account-mismatch refusal treats an UNKNOWN Desk
  // login as "let it through", so an auto-press fired before deskAccount() resolved would
  // skip the one check that stops a repost being authored by a colleague's extension login.
  useEffect(() => {
    if (!ext?.present || !deskAccReady || loading || !outbound || revalidating || staleErr) return
    const target = takeShareResume(listingId)
    if (target) run(target)
  }, [ext, deskAccReady, loading, outbound, revalidating, staleErr])   // eslint-disable-line react-hooks/exhaustive-deps

  // One required-field question (see requiredFor). Shared by the Share-button box and the planner.
  const needField = (k, who = null) => (
    <div key={k}>
      <label className="text-xs text-muted-foreground">{t(NEED_LABEL[k] || k)}
        {who && <span className="ml-1 opacity-70">· {who}</span>}</label>
      {FIELD_CHOICES[k]
        ? <select className={input} value={form?.[k] || ''} onChange={e => set(k, e.target.value)}>
            <option value="" disabled>—</option>
            {FIELD_CHOICES[k].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        : CHOICES[k]
        ? <select className={input} value={(form?.tagKeys || []).find(tk => CHOICES[k].includes(tk)) || ''}
            onChange={e => set('tagKeys', [...(form?.tagKeys || []).filter(tk => !CHOICES[k].includes(tk)), e.target.value])}>
            <option value="" disabled>—</option>
            {CHOICES[k].map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        : k === 'description'
        ? <textarea className={input} rows={3} value={form?.[k] ?? ''} onChange={e => set(k, e.target.value)} />
        : <input className={input} value={(k === 'price' ? effPrice : form?.[k]) ?? ''}
            inputMode={EDIT_FIELDS.some(f => f.k === k && f.numeric) ? 'decimal' : 'text'}
            onChange={e => set(k, e.target.value)} />}
    </div>
  )

  // ── ASK UP FRONT (owner 2026-10-14: "what other fields portals require and may be missing —
  // we ask it up front") ── Everything ANY of the three portals will refuse without, asked once
  // when the tab opens, instead of one portal at a time after its button is pressed. The press
  // check in run() stays as the backstop. A key stays on the list once asked (so a field does not
  // vanish under the agent's fingers after the first keystroke) and leaves only when no portal
  // requires it any more — e.g. rooms after the type is changed to land.
  const [asked, setAsked] = useState([])
  useEffect(() => {
    if (!outbound) return
    const miss = [...new Set(TARGETS.flatMap(tg => requiredFor(tg.key, outbound).filter(k => missingValue(k, outbound))))]
    setAsked(a => { const add = miss.filter(k => !a.includes(k)); return add.length ? [...a, ...add] : a })
  }, [outbound])
  const upfront = outbound
    ? asked.map(k => ({ k, who: TARGETS.filter(tg => requiredFor(tg.key, outbound).includes(k)) }))
        .filter(x => x.who.length).sort((a, b) => askRank(a.k) - askRank(b.k))
    : []
  const upfrontOpen = upfront.some(({ k }) => TARGETS.some(tg => requiredFor(tg.key, outbound).includes(k) && missingValue(k, outbound)))

  async function run(target) {
    // Only THIS platform's own run blocks it — a korter transfer must not hold ss hostage.
    if (!outbound || busy.has(target)) return
    // Never post a payload we have not just confirmed against the server — see the 🚨 on
    // the payload cache. Belt-and-braces behind the disabled buttons.
    if (revalidating || staleErr) return
    // Belt-and-braces behind the disabled button: a disabled control is a UI hint, and the
    // consequence here (listing authored by the wrong agent) is silent and awkward to undo.
    if (ext?.present && sameAccount(deskAcc, ext.account) === false) return
    // Missing required details → ask here, send nothing (see requiredFor).
    {
      const keys = requiredFor(target, outbound).filter(k => missingValue(k, outbound))
      const noPhotos = !(outbound.imageUrls || []).length
      // Blockers (core/reshare-readiness.mjs): a deal/type this portal does not offer, a value it
      // refuses, or — for a PHONE job only — a field the extension cannot fill (on the PC the agent
      // is there to finish it by hand). no_photos is already noPhotos.
      const blocks = blockers(target, outbound).filter(b => b.code !== 'no_photos' && (b.code !== 'not_automatable' || !ext?.present))
      if (keys.length || noPhotos || blocks.length) { setNeed({ target, keys, noPhotos, blocks }); return }
      if (need?.target === target) setNeed(null)
    }
    // busy FIRST: the wake-up ping below can take several seconds, and a button that does
    // nothing visible for that long is the friction this whole change exists to remove.
    setBusy(b => new Set(b).add(target))
    setResults(r => ({ ...r, [target]: null }))
    try {
      // ── SAVE THE COOPERATION FIRST, THE MOMENT THE BUTTON IS PRESSED ──────────────
      // Owner ruling 2026-08-01: "save cooperation right the moment button is pressed".
      // This used to sit AFTER the extension wake-up below, which left the form dirty and
      // unwritten for as long as that ping took — up to 7.5s on a cold service worker. With
      // portals now running in parallel, pressing Korter then SS.ge inside that stretch is
      // ordinary use, not a contrived double-click, and both presses would have read the
      // same pre-save baseline. Saving first shrinks the window to the save's own round
      // trip. It also means the terms survive even when the repost cannot run at all — the
      // agent typed them and pressed a button, so losing them to a missing extension was
      // always the wrong outcome (the same ruling that put this guard here: a press must
      // save what the agent forgot to).
      //
      // ⚠️ The shared promise STAYS. Saving earlier makes the window small; it does not
      // make it zero, and a double-click still lands inside one round trip. This is the
      // thing that actually guarantees one write — the ordering is defence in depth.
      //
      // Authorship is unaffected by running before the account check: a cooperation write is
      // authored by the DESK session's own token, not the extension's, so the mismatch below
      // governs who authors the LISTING and has no bearing on who recorded these terms.
      if (onBeforeRun) {
        if (!guardRef.current) guardRef.current = Promise.resolve(onBeforeRun())
        let flushed
        try { flushed = await guardRef.current } finally { guardRef.current = null }
        if (flushed && alive.current) setNote(t('desk_share_coop_autosaved'))
      }
      // ⚠️ The ping is a HINT, the transfer is the AUTHORITY. If we currently believe the
      // extension is absent, try once more RIGHT NOW instead of refusing: the usual cause is
      // a service worker that idled out, and this click itself wakes it. Refusing on a stale
      // negative is what made a healthy extension unusable. Only a second failure stops us,
      // and then the message names which of the three states we are actually in.
      // ── REMOTE: no extension in this browser, but one of the agent's own is online ──
      // A phone can never have the extension, so pinging it only ended in the stale-reload
      // ("it just refreshed", owner 2026-10-09). Hand the same `outbound` a local press would
      // send to the chosen browser instead; it runs the identical sntvRunDeskReshare.
      if (!ext?.present && remoteDevice) {
        if (runningJob) return     // belt-and-braces behind the disabled buttons
        let job
        try { ({ job } = await queueExtJob(remoteDevice.device_id, target, outbound)) }
        catch (e) {
          if (e?.code === 'busy') { if (alive.current) setResults(r => ({ ...r, [target]: { error: t('desk_share_busy') } })); return }
          throw e
        }
        const dev = remoteDevice.device_name
        if (alive.current) setResults(r => ({ ...r, [target]: { job, dev } }))
        const t0 = Date.now()
        while (alive.current && Date.now() - t0 < 15 * 60 * 1000) {
          await new Promise(res => setTimeout(res, 3000))
          const cur = (await getExtJob(job.id).catch(() => null))?.job
          if (!cur) continue
          // Every portal publishes itself on a phone job now (myhome, ss, korter), so only
          // 'published' ends the wait; 'filled' is a waypoint.
          const done = cur.state === 'published'
          if (alive.current) setResults(r => ({ ...r, [target]: { job: cur, dev, ok: done,
            reached: Math.max(r[target]?.reached ?? 0, JOB_RANK[cur.state] ?? 0),
            paid: r[target]?.paid || cur.state === 'paying' } }))
          // An error is NOT the end: ss stops at its pay page with one, and once the agent pays
          // on the PC the server moves the job to 'published' — keep watching until then.
          if (done || cur.state === 'expired') break
        }
        // Stopped watching without an end (page kept open 15 min): mark it so the portal buttons
        // — held while a job runs — come back instead of staying grey until a reload.
        if (alive.current) setResults(r => {
          const cur = r[target]?.job
          if (!cur || ['published', 'error', 'expired'].includes(cur.state)) return r
          return { ...r, [target]: { ...r[target], job: { ...cur, state: 'error', message: t('desk_share_job_no_update') } } }
        })
        return
      }
      if (!ext?.present) {
        const fresh = await pingExtension()
        if (alive.current) setExt(fresh)
        if (!fresh.present) {
          // Remember the press for after the auto-reload (see STALE → RELOAD). Only when that
          // reload will actually happen — otherwise a later manual refresh would fire it.
          if ((fresh.stale || !fresh.installed) && staleReloadAllowed()) {
            saveShareResume(listingId, target)
            // ⚠️ "No bridge at all" reloads too, but ONLY here, on a press — never from the
            // passive mount/focus ping, or an agent with no extension would get a reload every
            // time they open this tab. Owner-reported 2026-09-28: this state also shows when a
            // refresh DOES fix it — a tab opened before the extension was on/replaced has no
            // orphan left to mark itself 'stale', so it is indistinguishable from "not installed".
            // The 60s guard makes a genuinely missing extension cost one reload, then the banner.
            if (!fresh.stale) {
              try { sessionStorage.setItem(STALE_RELOAD_KEY, String(Date.now())) } catch { /* private mode */ }
              window.location.reload()
              return
            }
          }
          if (alive.current) setResults(r => ({ ...r, [target]: { error: t(fresh.stale ? 'desk_share_ext_stale' : fresh.installed ? 'desk_share_ext_waking' : 'desk_share_no_extension') } }))
          return
        }
        // The account gate must run against the answer we just got, not the stale null one.
        if (sameAccount(deskAcc, fresh.account) === false) return
      }
      await startReshare(target, outbound)
      if (alive.current) setResults(r => ({ ...r, [target]: { ok: true } }))
    } catch (e) {
      // The extension was reloaded AFTER our last good ping, so the press skipped the re-ping
      // above and went straight to startReshare. The orphaned bridge refuses it synchronously
      // (desk_bridge.js marks 'stale' and replies) — nothing reached the extension, so this is
      // the same state as a stale ping and gets the same treatment: the translated message,
      // plus the auto-reload + resume via setExt. Before this the agent saw a raw English
      // "Extension context invalidated." and nothing reloaded.
      // ⚠️ Narrower than pingExtension's regex on purpose: no bare "context" — a real transfer
      // error that merely mentions a context must not trigger a reload and a second press.
      if (/invalidat|Receiving end/i.test(e?.message || '')) {
        if (staleReloadAllowed()) saveShareResume(listingId, target)
        if (alive.current) {
          setExt({ present: false, installed: true, stale: true, version: null, account: null, error: e.message })
          setResults(r => ({ ...r, [target]: { error: t('desk_share_ext_stale') } }))
        }
        return
      }
      if (alive.current) setResults(r => ({ ...r, [target]: { error: e.message } }))
    } finally {
      if (alive.current) setBusy(b => { const n = new Set(b); n.delete(target); return n })
    }
  }

  // Says WHY it is slow — but ONLY when that is actually true. The AI runs server-side and
  // takes ~6s the first time a given listing is analysed (the ai_cache key folds in current
  // field values, so the extension's earlier analysis of the same ad usually does not hit).
  // Re-opens hit that cache: the payload is rebuilt from the DB, the model is not called.
  // A bare spinner for six seconds reads as broken — but so does a "the AI is working"
  // banner on a 1s cached load, which is what made re-opens look like repeated AI runs.
  if (loading) return (
    <div className="py-6 text-center space-y-1">
      <p className="text-sm text-muted-foreground">{t('desk_loading')}</p>
      {firstOpen && <p className="text-xs text-muted-foreground">{t('desk_share_loading_hint')}</p>}
    </div>
  )
  if (err)     return <p className="text-sm text-destructive py-6 text-center">{err}</p>
  if (!form)   return null

  const imgs = data.images || {}
  const prov = data.provenance || {}
  // sameAccount → true (same) · false (proven different) · null (one side unknown).
  // Only a proven difference blocks; unknown is allowed through. See the banner comment.
  const mismatch = ext?.present && sameAccount(deskAcc, ext.account) === false

  // Staging to Facebook needs a build that advertises it; every zip in the field predates
  // the feature and would swallow the message silently for two minutes. Composing and
  // copying need no extension at all, so only the ↗ button is gated — see supportsFbShare.
  // `ext === null` is "still pinging", not "unsupported": don't flash the update notice at
  // an agent whose extension is merely waking up.
  const fbCapable = supportsFbShare(ext)
  // ⚠️ "CANNOT" AND "HAS NOT ANSWERED YET" ARE DIFFERENT SENTENCES (2026-08-21). The first
  // form of this was `ext !== null && !fbCapable`, which told an agent to update software
  // that was merely ASLEEP — the exact mistake the amber banner above was rebuilt to stop
  // making, one panel further down. A capability answer only exists once the ping came back:
  //   ext === null      → still pinging; say nothing, disable nothing
  //   !ext.present      → asleep / orphaned / absent; the banner above already names which,
  //                       and the press itself re-pings (see the ↗ handler) — so this is NOT
  //                       an update problem and must never be worded as one
  //   present, no flag  → answered, and does not advertise fb_share: genuinely outdated
  const fbProvenIncapable = !!ext?.present && !fbCapable
  const fbNeedsUpdate = fbProvenIncapable

  const body = (
    <div className="space-y-4">
      {/* Say what this screen is, before the agent reads the numbers as a portal preview. */}
      <p className="text-xs text-muted-foreground">{t('desk_share_intro')}</p>

      <div className="flex items-center gap-3 flex-wrap text-xs">
        {/* When the payload was rebuilt from the origin (server `built_from_origin`), say so
            and link THAT ad — it is not the row the agent clicked, and when the photos or
            facts look wrong this is the page they need to open. */}
        {data.built_from_origin && data.listing?.source_url && (
          <a href={data.listing.source_url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-primary hover:underline max-w-full">
            <Icon name="external-link" />
            <span className="truncate">{t('desk_share_built_from_origin')}</span>
          </a>
        )}
        <button type="button" onClick={reloadPayload}
          className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
          <Icon name="refresh-cw" /> {t('desk_share_reload')}
        </button>
      </div>

      {/* Cache freshness. `refreshed` matters more than it looks: the agent is reading a
          form that just changed under them — and the one thing that did NOT change is
          whatever they had typed, because revalidation merges the server under `touched`. */}
      {refreshed && (
        <p className="text-xs text-amber-600 dark:text-amber-400">{t('desk_share_refreshed')}</p>
      )}
      {staleErr && (
        <p className="text-sm rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-400 flex items-center gap-2 flex-wrap">
          <Icon name="lock" /> {t('desk_share_unconfirmed')}
          <button type="button" className="underline" onClick={() => setReloadTick(n => n + 1)}>
            {t('desk_share_retry')}
          </button>
        </p>
      )}

      {/* Unsaved edits carried over from a previous open (localStorage draft). */}
      {restored && (
        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-2">
          {t('desk_share_draft_restored')}
          <button type="button" className="underline" onClick={discardDraft}>{t('desk_draft_discard')}</button>
        </p>
      )}

      {/* ── Ad copy ── the field agents actually rewrite, so it goes first and gets room. */}
      <div>
        <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
          {t('desk_share_description')} <ProvDot kind={prov.description} />
          {data.ai?.ran && (
            <span className="text-[11px] text-amber-600 dark:text-amber-400">
              · {t(data.ai.cached ? 'desk_share_ai_cached' : 'desk_share_ai_fresh')}
            </span>
          )}
        </label>
        <textarea className={input} rows={7} value={form.description || ''}
          onChange={e => set('description', e.target.value)} />
      </div>

      {/* ── Facts ── */}
      <div className={card}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {EDIT_FIELDS.map(f => (
            <div key={f.k}>
              {/* The server's provenance dot describes the PAYLOAD value. Once price is
                  inherited from the cooperation or hand-edited here, the payload is no
                  longer what the field shows — the line beneath states the real source
                  instead, so the stale dot is suppressed rather than left contradicting it. */}
              <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                {t(f.label)} <ProvDot kind={f.k === 'price' && (priceFromCoop || priceEdited) ? null : prov[f.k]} />
              </label>
              {f.k === 'price' ? (
                <div>
                  <div className="flex gap-1">
                    <input className={input} inputMode="decimal" value={effPrice ?? ''}
                      onChange={e => set('price', e.target.value)} />
                    <select className="rounded-lg border border-input px-2 text-sm bg-background"
                      value={effCurrency} onChange={e => set('priceUnit', e.target.value)}>
                      <option>USD</option><option>GEL</option>
                    </select>
                  </div>
                  {/* Say which link of the chain this number came from, and offer the way
                      back. An edited price that differs from the agreed one is legitimate
                      (net_amount shape) — hence a statement of fact, not a warning. */}
                  {priceFromCoop && !conversionRefused && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('desk_share_price_from_coop')}
                      {effCurrency !== upCurrency && ` · ${t('desk_share_price_converted').replace('{c}', upCurrency)}`}
                    </p>
                  )}
                  {conversionRefused && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{t('desk_share_no_fx')}</p>
                  )}
                  {priceEdited && (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                      <span>{t('desk_share_price_edited')}</span>
                      {agreedPrice != null && parseFloat(effPrice) !== agreedPrice && (
                        <span title={t('desk_share_price_differs')} className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                      )}
                      <button type="button" className="text-primary hover:underline" onClick={resetPrice}>
                        {t('desk_share_price_reset')}
                      </button>
                    </p>
                  )}
                </div>
              ) : f.k === 'landAreaSqM' ? (
                /* A PLOT NEEDS ITS UNIT (owner 2026-08-26, migration 0724). Georgian sellers
                   quote plots in ari (სოტკა = 100 m²) as often as in m², and no portal
                   publishes which — measured: 951 house yards sit under 100 "m²", where a
                   fractional value is 43× more common than above it. Without a unit those
                   numbers are unpublishable, so the post currently says nothing at all.
                   The agent is the only source that can settle it, and this is the last
                   screen before it goes out. '' = unknown, and unknown is sent as nothing —
                   never defaulted to m², which would turn a guess into an assertion. */
                <div className="flex gap-1">
                  <input className={input} inputMode="decimal"
                    value={form[f.k] ?? ''} onChange={e => set(f.k, e.target.value)} />
                  <select className="rounded-lg border border-input px-2 text-sm bg-background"
                    value={finalLandUnit} onChange={e => setFinalLandUnit(e.target.value)}>
                    <option value="">—</option>
                    <option value="sqm">მ²</option>
                    <option value="ari">სოტკა</option>
                    <option value="ha">ჰა</option>
                  </select>
                </div>
              ) : (
                <input className={input} inputMode={f.numeric ? 'decimal' : 'text'}
                  value={form[f.k] ?? ''} onChange={e => set(f.k, e.target.value)} />
              )}
            </div>
          ))}
        </div>

        {/* Deal / property type / condition — editable for the SHARE only (owner ruling
            2026-08-26). The old read-only warning still governs PORTAL reposts: each portal
            resolves types through getPropertyTypeSelection (mappings.js), so these selects
            deliberately do NOT write back into `form` — they ride card_fields to Orpheus and
            nothing else. Options are CORE vocabulary (what orpheus_facts speaks), never the
            extension's trade/lease words — the currency bug (d5b2ee3) is what sending a
            form-vocabulary value to a core-vocabulary reader looks like.
            Kind switches are the owner's allowed corrections only: a mislabelled apartment is
            really commercial space (or back), a "house" listing is really the plot. */}
        {/* Each select SHOWS the captured value as its selected option (owner: "it should say
            exactly what's selected, not a message"), so the mapping extension→core happens at
            display time and only a pick that DIFFERS from the captured value is sent.
            Condition is a building's attribute and hides on land (owner); land gets its own
            agricultural/non-agricultural status instead — keyed on the EFFECTIVE kind, so a
            house→land switch swaps the selects live. */}
        {(() => {
          const capturedDeal = { trade: 'sale', lease: 'rent', daily_rent: 'daily_rent', girao: 'girao' }[form.dealType] || ''
          const capturedKind = ['apartment', 'house', 'commercial', 'land'].includes(form.propertyType) ? form.propertyType : ''
          const effKind = finalKind || capturedKind
          const KIND_LBL = { apartment: 'ბინა', house: 'სახლი', commercial: 'კომერციული ფართი', land: 'მიწის ნაკვეთი' }
          const kindOpts = [...new Set([capturedKind,
            ...(capturedKind === 'apartment' || capturedKind === 'commercial' ? ['apartment', 'commercial'] : []),
            ...(capturedKind === 'house' ? ['house', 'land'] : [])])].filter(Boolean)
          const COND_OPTS = ['ახალი რემონტით', 'ძველი რემონტით', 'სარემონტო', 'ახალაშენებული', 'თეთრი კარკასი', 'შავი კარკასი', 'მწვანე კარკასი']
          const capturedCond = form.condition || ''
          const sel = "rounded-lg border border-input px-2 py-1 text-xs bg-background text-foreground"
          return (
            <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3 pt-3 border-t border-border/50 text-xs text-muted-foreground items-center">
              <label className="flex items-center gap-1.5">{t('desk_share_f_deal')}:
                <select className={sel} value={finalDeal || capturedDeal}
                        onChange={e => setFinalDeal(e.target.value === capturedDeal ? '' : e.target.value)}>
                  {!capturedDeal && <option value="">—</option>}
                  <option value="sale">იყიდება</option>
                  <option value="rent">ქირავდება</option>
                  <option value="daily_rent">ქირავდება დღიურად</option>
                  <option value="girao">გირავდება</option>
                </select>
              </label>
              <label className="flex items-center gap-1.5">{t('desk_share_f_kind')}:
                <select className={sel} value={effKind}
                        onChange={e => {
                          const v = e.target.value
                          setFinalKind(v === capturedKind ? '' : v)
                          // the selects that depend on kind must not carry a stale pick across
                          if (v === 'land') setFinalCond(''); else setFinalLand('')
                        }}>
                  {!capturedKind && <option value="">—</option>}
                  {kindOpts.map(k => <option key={k} value={k}>{KIND_LBL[k]}</option>)}
                </select>
              </label>
              {effKind !== 'land' && (
                <label className="flex items-center gap-1.5">{t('desk_share_f_condition')}:
                  <select className={sel} value={finalCond || capturedCond}
                          onChange={e => setFinalCond(e.target.value === capturedCond ? '' : e.target.value)}>
                    {(!capturedCond || !COND_OPTS.includes(capturedCond)) && <option value={capturedCond}>{capturedCond || '—'}</option>}
                    {COND_OPTS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              )}
              {effKind === 'land' && (
                <label className="flex items-center gap-1.5">{t('desk_share_f_land_status')}:
                  <select className={sel} value={finalLand} onChange={e => setFinalLand(e.target.value)}>
                    <option value="">—</option>
                    <option value="agricultural">სასოფლო-სამეურნეო</option>
                    <option value="non_agricultural">არასასოფლო-სამეურნეო</option>
                  </select>
                </label>
              )}
              {form.cadastralCode && <span>{t('desk_share_f_cadastral')}: <b className="text-foreground">{form.cadastralCode}</b></span>}
            </div>
          )
        })()}
      </div>

      {/* Sits directly under the facts, because the question it answers ("what have I
          advertised this at before?") is one an agent asks while setting the price above —
          not one they go looking for on another screen. Collapsed and lazy: see the file. */}
      <AdvertisingHistory propertyId={data.listing?.property_id || null} />

      {/* ── Photos ── the gallery itself (owner request 2026-07-31): review what goes out,
          click to exclude, click again to restore. Excluded thumbs stay visible (dimmed)
          rather than disappearing — the removal is a choice about THIS repost, not a fact
          about the property, and it must stay reversible until a portal button fires. */}
      <div className={card}>
        <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_share_photos')}</p>
        <p className="text-sm">
          {t('desk_share_photos_count').replace('{n}', photos.filter(u => !removed.has(u)).length)}
          {removed.size > 0 && (
            <span className="text-xs text-muted-foreground ml-2">
              · {t('desk_share_photos_excluded').replace('{n}', removed.size)}
            </span>
          )}
        </p>
        {photos.length > 0 && (
          <>
            {/* Two modes so a phone never guesses what a tap does: normal = tap excludes /
                restores (unchanged), Reorder = tap the left/right half to move, ★ to make main.
                Tiles grow in Reorder mode so the halves are real thumb targets. */}
            {/* PHONE ONLY (owner 2026-10-16: "show it only on the phone"). "Phone" = this page has no
                extension, the same test that turns the Share buttons into jobs for the PC; hidden
                while the extension check is still running (ext === null), so a PC never flashes it. */}
            {canReorder && <div className="mt-2 flex flex-wrap items-center gap-2">
              <button type="button" className={btn(reordering ? 'primary' : 'ghost')}
                onClick={() => setReordering(r => !r)}>
                {reordering ? t('desk_share_photos_reorder_done') : t('desk_share_photos_reorder')}
              </button>
              {order && (
                <button type="button" className="text-xs underline text-muted-foreground" onClick={() => setOrder(null)}>
                  {t('desk_share_photos_reorder_reset')}
                </button>
              )}
            </div>}
            <div className={`grid gap-1.5 mt-2 ${reorderOn ? 'grid-cols-3 sm:grid-cols-4' : 'grid-cols-4 sm:grid-cols-6'}`}>
              {(() => { const mainUrl = photos.find(x => !removed.has(x)); return photos.map((u, i) => {
                const off = removed.has(u)
                const main = u === mainUrl
                if (reorderOn) return (
                  <div key={u} className="relative rounded-md overflow-hidden">
                    <img src={u} alt="" loading="lazy" className={`h-24 w-full object-cover ${off ? 'opacity-30 grayscale' : ''}`} />
                    <button type="button" aria-label={t('desk_share_photos_left')} disabled={i === 0}
                      className="absolute inset-y-0 left-0 w-1/2 flex items-center justify-start pl-1 text-white text-lg disabled:opacity-0 bg-gradient-to-r from-black/40 to-transparent"
                      onClick={() => movePhoto(u, i - 1)}>◀</button>
                    <button type="button" aria-label={t('desk_share_photos_right')} disabled={i === photos.length - 1}
                      className="absolute inset-y-0 right-0 w-1/2 flex items-center justify-end pr-1 text-white text-lg disabled:opacity-0 bg-gradient-to-l from-black/40 to-transparent"
                      onClick={() => movePhoto(u, i + 1)}>▶</button>
                    <button type="button" aria-label={t('desk_share_photos_make_main')} disabled={main || off}
                      className={`absolute top-1 left-1/2 -translate-x-1/2 rounded-full px-2 py-1 text-xs font-medium ${main ? 'bg-emerald-600 text-white' : 'bg-black/60 text-white'} disabled:cursor-default`}
                      onClick={() => movePhoto(u, 0)}>
                      {main ? t('desk_share_photos_main') : '★'}
                    </button>
                  </div>
                )
                return (
                  <button key={u} type="button" title={t('desk_share_photos_hint')}
                    className="relative rounded-md overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary"
                    onClick={() => setRemoved(prev => {
                      const next = new Set(prev)
                      if (next.has(u)) next.delete(u); else next.add(u)
                      return next
                    })}>
                    <img src={u} alt="" loading="lazy"
                      className={`h-16 w-full object-cover ${off ? 'opacity-30 grayscale' : ''}`} />
                    {off && (
                      <span className="absolute inset-0 flex items-center justify-center text-destructive text-lg font-bold">✕</span>
                    )}
                    {main && (
                      <span className="absolute bottom-0 inset-x-0 bg-emerald-600/90 text-white text-[10px] leading-4 text-center">{t('desk_share_photos_main')}</span>
                    )}
                  </button>
                )
              }) })()}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">{t(reorderOn ? 'desk_share_photos_reorder_hint' : 'desk_share_photos_hint')}</p>
          </>
        )}
        {imgs.dropped_watermarked > 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            <Icon name="lock" /> {t('desk_share_photos_watermarked').replace('{n}', imgs.dropped_watermarked)}
          </p>
        )}
        {imgs.used_thumbs && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{t('desk_share_photos_thumbs')}</p>
        )}
        {(imgs.total ?? 0) === 0 && (
          <p className="text-xs text-destructive mt-1">{t('desk_share_photos_none')}</p>
        )}
      </div>

      {/* ── Targets ── */}
      <div>
        {/* Three different states, three different fixes — the old single line said
            "install it" for all of them, including to agents watching the extension work.
            None of these disables the buttons any more: pressing one re-pings first. */}
        {ext && !ext.present && !remoteDevice && (
          <p className="text-sm rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-400 mb-2">
            <Icon name="lock" />{' '}
            {t(ext.stale ? 'desk_share_ext_stale' : ext.installed ? 'desk_share_ext_waking' : 'desk_share_no_extension')}
            {/* 'waking' gets none — pressing a portal button again is its fix. */}
            {(ext.stale || !ext.installed) && (
              <>{' '}<button type="button" className="underline font-medium" onClick={() => window.location.reload()}>
                {t('desk_share_ext_reload')}
              </button></>
            )}
          </p>
        )}
        {/* ── ACCOUNT MISMATCH: refuse, don't warn ────────────────────────────
            Every core write on this path is authored by the EXTENSION's token — it
            stamps the listing's author, raw_listing.scraped_by, and with them the
            ownership and ranking credit. The Desk session has no say. So a repost
            fired while the two are different logins silently hands the listing to a
            colleague, and the agent gets no signal at all: the portal says posted,
            their Desk looks normal. Undoing it later means editing lineage rows.
            ⚠️ Blocked ONLY on a proven mismatch. If either side is unknown we let it
            through — refusing on absence of proof would strand every agent whose
            token carries no email claim, and this check is a safety net, not a gate
            on the feature existing. */}
        {mismatch === true && (
          <div className="text-sm rounded border border-destructive/50 bg-destructive/10 px-3 py-2 mb-2 space-y-1">
            <p className="font-medium text-destructive"><Icon name="lock" /> {t('desk_share_account_mismatch')}</p>
            <p className="text-xs text-muted-foreground">
              {t('desk_share_account_desk')}: <b className="text-foreground">{deskAcc}</b>
              {' · '}
              {t('desk_share_account_ext')}: <b className="text-foreground">{ext?.account}</b>
            </p>
            <p className="text-xs text-muted-foreground">{t('desk_share_account_fix')}</p>
          </div>
        )}
        {!need && upfront.length > 0 && (
          <div className={`mb-2 rounded border px-3 py-2 text-sm space-y-2 ${upfrontOpen ? 'border-amber-500/50 bg-amber-500/10' : 'border-emerald-500/40 bg-emerald-500/5'}`}>
            <p className={`font-medium ${upfrontOpen ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
              {t(upfrontOpen ? 'desk_share_upfront_title' : 'desk_share_upfront_done')}
            </p>
            {upfront.map(({ k, who }) => needField(k, who.length === TARGETS.length ? null : who.map(x => x.label).join(', ')))}
          </div>
        )}
        {/* What a portal will refuse whatever is answered above (core/reshare-readiness.mjs blockers),
            shown before any press — also when nothing is missing, so the agent never finds out only
            after pressing. not_automatable is a phone-job matter and is said at the press. */}
        {!need && outbound && (() => {
          const bl = TARGETS.flatMap(tg => blockers(tg.key, outbound).filter(b => b.code !== 'no_photos' && b.code !== 'not_automatable')
            .map(b => ({ ...b, tg: tg.key })))
          return bl.length > 0 && (
            <div className="mb-2 rounded border border-destructive/40 bg-destructive/5 px-3 py-2 space-y-1">
              {bl.map(b => <p key={b.tg + b.code} className="text-xs text-destructive">{blockText(b, b.tg)}</p>)}
            </div>
          )
        })()}
        {need && (
          <div className="mb-2 rounded border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm space-y-2">
            <p className="font-medium text-amber-700 dark:text-amber-400">
              {t('desk_share_need_title').replace('{p}', TARGETS.find(x => x.key === need.target)?.label || need.target)}
            </p>
            {[...new Set([...need.keys, ...(need.blocks || []).map(b => b.key).filter(Boolean)])].map(needField)}
            {need.noPhotos && <p className="text-xs text-destructive">{t('desk_share_need_photos')}</p>}
            {(need.blocks || []).map(b => (
              <p key={b.code} className="text-xs text-destructive">{blockText(b, need.target)}</p>
            ))}
            <button type="button" className={btn('primary')} disabled={need.noPhotos || (need.blocks || []).some(b => !b.key)}
              onClick={() => run(need.target)}>
              {t('desk_share_need_continue')}
            </button>
          </div>
        )}
        <BrowserSessions selectable={!ext?.present} selected={remoteDevice?.device_id || null}
          onSelect={setDeviceId} onSessions={setBrowsers} />
        {/* Scheduled shares (owner 2026-10-09: "they must also be asked questions that will be needed
            to complete reshare"). The planner asks the SAME required fields the buttons do, inline,
            for the portals ticked there. Answers edit this form, so `outbound` (what the plan
            stores) already carries them. */}
        {outbound && (
          <SharePlanner listingId={listingId} propertyId={data?.listing?.property_id || null}
            outbound={outbound} device={remoteDevice} renderField={needField}
            missingFor={target => requiredFor(target, outbound).filter(k => missingValue(k, outbound))}
            blocksFor={target => blockers(target, outbound).filter(b => b.code !== 'no_photos').map(b => blockText(b, target))} />
        )}
        <div className="flex flex-wrap gap-2">
          {TARGETS.map(tg => {
            const r = results[tg.key]
            return (
              <button key={tg.key} className={btn('ghost')}
                disabled={busy.has(tg.key) || mismatch === true || revalidating || !!staleErr || (!ext?.present && !!runningJob)}
                onClick={() => run(tg.key)}>
                <span className={tg.tone}>●</span> {tg.label}
                {busy.has(tg.key) && <span className="ml-1 text-xs text-muted-foreground">…</span>}
                {r?.ok && <span className="ml-1 text-emerald-500"><Icon name="check" /></span>}
              </button>
            )
          })}
        </div>
        {/* ── Facebook (B894) — NOT a portal transfer: no extension, no core writes, no
            account-mismatch stakes. The server composes the post from the FACT PACKAGE
            (generate-once cache; a fact change re-renders exactly once) and the agent
            copies it into the FB composer. fill_facebook.js (text+photos in one act) is
            the B894 Phase-4 follow-up; until then copy is the whole flow. */}
        <div className="mt-2">
          <button className={btn('ghost')} disabled={fbBusy}
            onClick={() => setFbMenu(v => !v)}>
            <span className="text-blue-500">●</span> Facebook
            {/* ✦ ORPHEUS IS WRITING (owner ruling 2026-08-27). The byline below names him
                once the post EXISTS; the agent watching a spinner had no idea who was working,
                which is the moment the question is actually asked. Same mark, same hue, no
                second component — and it still never leaves the Desk. */}
            {fbBusy && (
              <span className="ml-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                <AiMark mark={{ agent: 'orpheus', action: 'authored', note: t('desk_share_fb_by_orpheus_note') }} />
                {t('desk_share_fb_generating')}
              </span>
            )}
          </button>
          {fbMenu && !fb && (
            <div className="mt-2 rounded-lg border border-border p-3 space-y-2 max-w-md">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={fbOpts.contact}
                  onChange={e => setFbOpts(o => ({ ...o, contact: e.target.checked }))} />
                {t('desk_share_fb_opt_contact')}
              </label>
              {/* §7/T25/T26: WHO gets published as a contact, and T27's enforcement made real.
                  Owner: "When agent shares on Facebook they must be provided automatically their
                  assigned phone number… If admin or manager shares, they are asked: your number,
                  or all agents working on this property." And the reason, which is why this is a
                  multi-select and not a dropdown: "if one doesn't answer another will, so client
                  won't be lost."
                  🔴 The list comes from the SERVER, already filtered by opt-out (§8: an agent who
                  opted out "does not appear in the picker at all — not greyed, not
                  unchecked-by-default. Absent."). It is never filtered here: a second copy of
                  that rule would drift, and the drifting copy is the one that publishes a number
                  somebody asked us not to publish. */}
              {fbOpts.contact && (
                <div className="space-y-1">
                  {fbContacts === null && <p className="text-xs text-muted-foreground">{t('desk_loading')}</p>}
                  {(fbContacts || []).map(c => (
                    <label key={c.party_id} className={`flex items-center gap-2 text-sm ${c.phone ? 'cursor-pointer' : 'opacity-60'}`}>
                      <input type="checkbox" disabled={!c.phone}
                        checked={fbPick.includes(c.party_id)}
                        onChange={e => setFbPick(p => e.target.checked ? [...p, c.party_id] : p.filter(x => x !== c.party_id))} />
                      <span>{c.name || '—'}</span>
                      {/* A contact with no number is SHOWN and disabled rather than dropped. §7:
                          "If they have no assignment, say so — do not silently fall back." A
                          silently shorter list reads as "nobody else works this flat". */}
                      <span className="tabular-nums text-muted-foreground">{c.phone || t('desk_share_fb_contact_none')}</span>
                      {c.source === 'assigned' && <span className="text-[10px] text-muted-foreground/70">{t('desk_share_fb_contact_assigned')}</span>}
                    </label>
                  ))}
                  {fbContacts && fbContacts.length === 0 && (
                    <p className="text-xs text-muted-foreground">{t('desk_share_fb_contact_empty')}</p>
                  )}
                  {/* The typed box survives as an EXPLICIT extra, not as the default it used to
                      be. It is how an admin adds a number the roster does not hold, and it is
                      still sent — but it no longer decides the post on its own. */}
                  <input className={input + ' text-sm'} placeholder={t('desk_share_fb_contact_ph')}
                    value={fbPhone} onChange={e => {
                      setFbPhone(e.target.value)
                      try { localStorage.setItem('sntv_share_contact', e.target.value) } catch { /* private mode */ }
                    }} />
                </div>
              )}
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={fbOpts.price}
                  onChange={e => setFbOpts(o => ({ ...o, price: e.target.checked }))} />
                {t('desk_share_fb_opt_price')}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={fbOpts.exact}
                  onChange={e => setFbOpts(o => ({ ...o, exact: e.target.checked }))} />
                {t('desk_share_fb_opt_exact')}
              </label>
              <div className="flex justify-end">
                <button className={btn('primary')} disabled={fbBusy}
                  onClick={async () => {
                    setFbBusy(true); setFbErr(null); setFbCopied(false)
                    try {
                      // THE RESHARE BOX WINS (owner 2026-08-20): whatever the agent edited
                      // in this form — price, rooms, floors… — is the post's truth, exactly as
                      // it is for a portal repost. card_fields is the fact package's VERIFIED
                      // overlay; edited values change the fact hash, so an edited post renders
                      // fresh while unedited agents share the cached one.
                      const cf = {}
                      // dealType stays OFF the overlay: the box shows it read-only in EXTENSION vocabulary
                      // ('trade'), which the renderer would misread — core's intent is already right.
                      for (const k of ['roomsCount', 'bedroomsCount',
                                       'bathroomCount', 'areaSqM', 'landAreaSqM', 'aptFloor',
                                       'totalFloors', 'ceilingHeight', 'condition']) {
                        const v = form?.[k]
                        if (v !== undefined && v !== null && v !== '') cf[k] = v
                      }
                      // ⚠️ PRICE AND CURRENCY COME FROM WHAT THE AGENT SEES, not from raw form
                      // keys (owner-caught 2026-08-21: the box read GEL 1,350 and the post said
                      // $1,350). TWO separate reasons, both silent:
                      //   · the currency key is `priceUnit`, and this loop asked for `currency` —
                      //     a key that does not exist, so it was never sent at all and the render
                      //     fell back to the listing's stored currency;
                      //   · `effPrice`/`effCurrency` are the DISPLAYED pair after inheritance and
                      //     conversion, so an unedited-but-converted price differs from
                      //     `form.price`. Sending the raw field would publish a number the agent
                      //     never saw.
                      // The whole point of the overlay is that the final page is the truth, and
                      // the final page shows these two.
                      if (effPrice !== '' && effPrice !== null && effPrice !== undefined) cf.price = effPrice
                      if (effCurrency) cf.currency = effCurrency
                      // Final-page overrides (owner 2026-08-26): a deliberate pick beats the
                      // captured value — including form.condition, which the loop above may
                      // have set, so the condition override must land AFTER it.
                      if (finalDeal) cf.dealType = finalDeal
                      if (finalKind && finalKind !== form.propertyType) cf.propertyKind = finalKind
                      if (finalCond) cf.condition = finalCond
                      if (finalLandUnit) cf.landAreaUnit = finalLandUnit
                      // Land carries a STATUS, never a condition (owner): when the share's
                      // effective kind is land, drop any condition the loop picked up from the
                      // captured form — a plot with "ახალი რემონტით" is a claim about nothing.
                      const shareKind = finalKind || form.propertyType
                      if (shareKind === 'land') { delete cf.condition; if (finalLand) cf.landStatus = finalLand }
                      const r = await getOrpheusRenders(srcId, {
                        targets: ['fb_ka', 'fb_en'],
                        // §7: name+number pairs, so a post can carry several. The typed box is
                        // appended as an unnamed extra rather than replacing the picked list.
                        // ⚠️ `contact` stays populated with the FIRST entry — a cross-repo contract:
                        // extensions in agents' browsers predate `contacts` and would otherwise post
                        // a property with no number on it at all.
                        contacts: fbOpts.contact ? fbShareContacts(fbContacts, fbPick, fbPhone) : undefined,
                        contact: fbOpts.contact ? (fbShareContacts(fbContacts, fbPick, fbPhone)[0]?.phone || undefined) : undefined,
                        share_contact: fbOpts.contact,
                        share_price: fbOpts.price,
                        share_exact_location: fbOpts.exact,
                        card_fields: cf,
                      })
                      const bad = (r.renders || []).filter(x => !x.text)
                      if (!r.renders?.length || bad.length === (r.renders || []).length)
                        setFbErr((bad[0]?.reasons || [bad[0]?.error || 'render failed']).join(', '))
                      else setFb(r)
                    } catch (e) { setFbErr(e.message) }
                    finally { setFbBusy(false) }
                  }}>
                  {t('desk_share_fb_compose')}
                </button>
              </div>
            </div>
          )}
          {fbErr && <p className="text-xs text-destructive mt-1.5">{fbErr}</p>}
          {fb && (
            <div className="mt-1.5"><button type="button" className="text-xs text-muted-foreground hover:text-foreground underline"
              onClick={() => { setFb(null); setFbCopied(false) }}>{t('desk_share_fb_change_opts')}</button></div>
          )}
          {fb && (
            <div className="mt-2 rounded-lg border border-border p-2 space-y-2">
              <div className="flex items-center gap-2">
                {(fb.renders || []).filter(x => x.text).map(x => (
                  <button key={x.target} type="button"
                    className={`text-xs px-2 py-1 rounded ${fbLang === x.target ? 'bg-primary/15 text-foreground font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => { setFbLang(x.target); setFbCopied(false) }}>
                    {x.target === 'fb_en' ? 'English' : 'ქართული'}
                  </button>
                ))}
                {/* ✦ ORPHEUS WROTE THIS (owner ruling 2026-08-27: "wherever Orpheus writes a
                    Facebook post or a description, it must say Orpheus is doing it").
                    ⚠️ The mark marks the COMPOSE SURFACE, never the output. The post itself
                    carries no attribution — an advert is the agency's word, not an agent's
                    byline — and ✦ never leaves the Desk (owner, 2026-08-19). Copying the text
                    or handing it to the extension takes the words and leaves the mark behind.
                    The tooltip carries the template version, which is the honest account of
                    what wrote it: the body is BUILT, and the model's only job is extracting
                    amenity nouns from the seller's own prose. */}
                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                  <AiMark mark={{
                    agent: 'orpheus',
                    action: 'authored',
                    note: t('desk_share_fb_by_orpheus_note'),
                    version: (fb.renders || []).find(x => x.target === fbLang)?.template_version || null,
                  }} />
                  {t('desk_share_fb_by_orpheus')}
                </span>
                <span className="flex-1" />
                <button type="button" className={btn('ghost')}
                  onClick={async () => {
                    const txt = (fb.renders || []).find(x => x.target === fbLang)?.text || ''
                    try { await navigator.clipboard.writeText(txt); setFbCopied(true) } catch { /* keep panel */ }
                  }}>
                  {fbCopied ? t('desk_share_fb_copied') : t('desk_share_fb_copy')}
                </button>
                <button type="button" className={btn('primary')}
                  disabled={fbOpened === 'sending' || fbProvenIncapable}
                  title={fbProvenIncapable ? t('desk_share_fb_needs_update') : undefined}
                  onClick={async () => {
                    // Hand the kept photo set + EVERY rendered language to the extension;
                    // fill_facebook.js does the composer work on facebook.com. Human presses Post.
                    // `text` stays the selected language so an extension that predates `texts`
                    // behaves exactly as before; `texts` lets a newer one offer the choice per
                    // group without a second press (a second press = a second sessionId = a
                    // second publication, which the owner ruled against). See extensionBridge.
                    setFbOpened('sending')
                    try {
                      // ⚠️ THE PING IS A HINT — same doctrine as run() above. A believed-absent
                      // extension is usually a service worker that idled out, and this very
                      // click wakes it; refusing on the stale negative is what made a healthy
                      // extension look broken. Ping again, and only a SECOND failure stops us,
                      // named as the state it actually is rather than as "outdated".
                      if (!ext?.present) {
                        const fresh = await pingExtension()
                        if (alive.current) setExt(fresh)
                        if (!fresh.present) {
                          setFbOpened({ error: t(fresh.stale ? 'desk_share_ext_stale'
                            : fresh.installed ? 'desk_share_ext_waking' : 'desk_share_no_extension') })
                          return
                        }
                        // It woke — but waking says nothing about what it CAN do. A build with
                        // no fb_share must still not be sent a message it will never answer.
                        if (!supportsFbShare(fresh)) {
                          setFbOpened({ error: t('desk_share_fb_needs_update') })
                          return
                        }
                      }
                      const { text, texts, defaultTarget } = fbShareTexts(fb.renders, fbLang)
                      const imgs = photos.filter(u => !removed.has(u)).slice(0, 10)
                      const r = await startFacebookShare({ text, texts, defaultTarget, imageUrls: imgs,
                        // identity for the post-back: when the agent presses Post, the
                        // extension captures the group permalink and registers it in core
                        // (register_publication) + the activity log — same rail as portals.
                        listingId: data?.listing?.id || srcId,
                        propertyId: data?.listing?.property_id || null,
                        // WHICH GROUP (owner 2026-09-02: land has its own). The extension falls
                        // back to the apartments group when this is absent, so an older build
                        // that ignores it is wrong-but-working rather than broken. The kind is
                        // the EFFECTIVE one — an agent who corrected house→land on this very
                        // form means land, and the share must follow the correction, not the
                        // capture.
                        fbUrl: fbGroupForKind(finalKind || form.propertyType) })
                      setFbOpened({ images: r.images ?? imgs.length })
                    } catch (e) { setFbOpened({ error: e.message }) }
                  }}>
                  {fbOpened === 'sending' ? t('desk_share_fb_opening') : t('desk_share_fb_open')}
                </button>
              </div>
              {/* The disabled ↗ needs to say WHY next to itself. A `title` alone would not
                  do: a disabled button fires no mouse events, so the tooltip never opens. */}
              {fbNeedsUpdate && (
                <p className="text-xs text-muted-foreground">{t('desk_share_fb_needs_update')}</p>
              )}
              {fbOpened && fbOpened !== 'sending' && (
                <p className={'text-xs ' + (fbOpened.error ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400')}>
                  {fbOpened.error
                    ? fbOpened.error
                    : t('desk_share_fb_opened').replace('{n}', String(fbOpened.images))}
                </p>
              )}
              {/* Sized to the post (up to a tall cap) and grab-resizable — a fixed 10 rows
                  hid half the text behind a scrollbar (owner, 2026-08-20). */}
              <textarea readOnly
                rows={Math.min(28, (((fb.renders || []).find(x => x.target === fbLang)?.text || '').split('\n').length + 2))}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs font-mono resize-y"
                value={(fb.renders || []).find(x => x.target === fbLang)?.text || ''} />
            </div>
          )}
        </div>
        {/* Explains the one moment the buttons are disabled for a reason the agent cannot
            see. Without this the cache's whole benefit reads as a broken button instead. */}
        {revalidating && <p className="text-xs text-muted-foreground mt-1.5">{t('desk_share_confirming')}</p>}
        {note && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5">{note}</p>}
        {!ext?.present && runningJob && (
          <p className="text-xs text-muted-foreground mt-2">
            {t('desk_share_wait_for').replace('{p}', TARGETS.find(x => x.key === runningJob[0])?.label || runningJob[0])}
          </p>
        )}
        {Object.entries(results).filter(([, r]) => r?.job).map(([k, r]) => (
          <JobSteps key={'job-' + k} portal={TARGETS.find(x => x.key === k)?.label || k} r={r} />
        ))}
        {Object.entries(results).filter(([, r]) => r?.error).map(([k, r]) => (
          <p key={k} className="text-xs text-destructive mt-1.5">{k}: {r.error}</p>
        ))}
        {/* One portal at a time, on purpose: each transfer opens a tab and fills a form with
            the agent watching, and the extension holds a per-platform lock anyway. Firing
            three at once would just queue them while looking like it had not. */}
        <p className="text-xs text-muted-foreground mt-2">{t('desk_share_one_at_a_time')}</p>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button className={btn('ghost')} onClick={onClose}>{t('desk_lineage_close_button')}</button>
      </div>
    </div>
  )

  // Body only — DealEditor owns the modal chrome, same as the other two tabs.
  return body
}
